const SITE_URL = "https://georgiadream.ge";

// Owner identity used as Article.author. Airbnb profile URL doubles as a
// verifiable sameAs (Superhost since 2023, 5.0/54 reviews — visible on
// the profile page). Pulled out as a constant so future updates touch
// one place.
const OWNER = {
  name: "Ilya",
  url: "https://www.airbnb.com/users/profile/1465883899966328902",
  jobTitle: "Owner & Host",
} as const;

export interface AboutPageData {
  title?: string;
  meta_description?: string;
  description?: string;
  hero_image?: string;
  og_image?: string;
  last_updated?: string;
  corners_gallery?: {
    items?: Array<{
      title?: string;
      caption?: string;
      image?: string;
    }>;
  };
}

export interface AboutLdConfig {
  pagePath: string; // e.g. "/about" or "/ru/about"
  homePath: string; // e.g. "/" or "/ru"
  homeLabel: string; // localized "Home"
}

function abs(src?: string): string | undefined {
  if (!src) return undefined;
  return src.startsWith("http") ? src : `${SITE_URL}${src}`;
}

// Emits an AboutPage node that references the authoritative LodgingBusiness
// (#lodgingbusiness, declared once on the homepage) rather than redeclaring
// the full lodging graph. Keeps Google's understanding coherent across the
// site.
export function buildAboutLd(page: AboutPageData, cfg: AboutLdConfig) {
  const pageUrl = `${SITE_URL}${cfg.pagePath}`;
  const homeUrl =
    cfg.homePath === "/" ? `${SITE_URL}/` : `${SITE_URL}${cfg.homePath}/`;
  const lodgingId = `${SITE_URL}/#lodgingbusiness`;
  const image = abs(page.og_image) || abs(page.hero_image);

  const graph: Array<Record<string, unknown>> = [];

  graph.push({
    "@type": "BreadcrumbList",
    "@id": `${pageUrl}#breadcrumbs`,
    itemListElement: [
      { "@type": "ListItem", position: 1, name: cfg.homeLabel, item: homeUrl },
      {
        "@type": "ListItem",
        position: 2,
        name: page.title || "About",
        item: pageUrl,
      },
    ],
  });

  graph.push({
    "@type": "AboutPage",
    "@id": `${pageUrl}#webpage`,
    url: pageUrl,
    name: page.title,
    description: page.meta_description || page.description,
    ...(image ? { primaryImageOfPage: image, image } : {}),
    breadcrumb: { "@id": `${pageUrl}#breadcrumbs` },
    mainEntity: { "@id": lodgingId },
    about: { "@id": lodgingId },
    isPartOf: { "@id": `${SITE_URL}/#website` },
    inLanguage:
      cfg.pagePath.startsWith("/ru")
        ? "ru"
        : cfg.pagePath.startsWith("/ka")
          ? "ka"
          : "en",
    speakable: {
      "@type": "SpeakableSpecification",
      cssSelector: ["#about-hero-title", "[data-speakable]"],
    },
  });

  // Article node — strengthens E-E-A-T by attributing the page to a real
  // person (owner Ilya, Airbnb Superhost) rather than an anonymous brand.
  // Article + AboutPage co-exist: AboutPage is the page-type semantic,
  // Article is the editorial-content semantic. Google understands both.
  // dateModified comes from the page frontmatter; if missing, no date is
  // emitted (Google prefers absent over wrong).
  const articleNode: Record<string, unknown> = {
    "@type": "Article",
    "@id": `${pageUrl}#article`,
    headline: page.title,
    ...(page.meta_description || page.description
      ? { description: page.meta_description || page.description }
      : {}),
    ...(image ? { image } : {}),
    author: {
      "@type": "Person",
      "@id": `${SITE_URL}/#owner`,
      name: OWNER.name,
      jobTitle: OWNER.jobTitle,
      url: OWNER.url,
      sameAs: [OWNER.url],
    },
    publisher: { "@id": `${SITE_URL}/#organization` },
    isPartOf: { "@id": `${pageUrl}#webpage` },
    mainEntityOfPage: { "@id": `${pageUrl}#webpage` },
    inLanguage:
      cfg.pagePath.startsWith("/ru")
        ? "ru"
        : cfg.pagePath.startsWith("/ka")
          ? "ka"
          : "en",
  };
  if (page.last_updated) {
    const luIso = new Date(page.last_updated).toISOString();
    if (Number.isFinite(new Date(page.last_updated).getTime())) {
      articleNode.dateModified = luIso;
    }
  }
  graph.push(articleNode);

  // Index-based @id to keep IDs stable across RU/KA (title slug → NFKD strips
  // non-ASCII and would collide on "#corner-item" for every corner).
  const corners = page.corners_gallery?.items ?? [];
  for (let idx = 0; idx < corners.length; idx++) {
    const corner = corners[idx];
    if (!corner?.title) continue;
    graph.push({
      "@type": "TouristAttraction",
      "@id": `${pageUrl}#corner-${idx + 1}`,
      name: corner.title,
      ...(corner.caption ? { description: corner.caption } : {}),
      ...(corner.image ? { image: abs(corner.image) } : {}),
      containedInPlace: { "@id": lodgingId },
    });
  }

  return {
    "@context": "https://schema.org",
    "@graph": graph,
  };
}
